class ProductBundle extends HTMLElement {
  connectedCallback() {
    this.onFormVariantChange = this.sync.bind(this);
    this.onInternalChange = this.sync.bind(this);
    this.childrenConfig = this.parseChildrenConfig();
    this.bindForm();
    this.addEventListener("change", this.onInternalChange);
    this.sync();
  }

  disconnectedCallback() {
    this.form?.removeEventListener("variant:change", this.onFormVariantChange);
    this.removeEventListener("change", this.onInternalChange);
  }

  bindForm() {
    const formId = this.getAttribute("form");
    const form = document.getElementById(formId) || document.forms[formId];
    if (form === this.form) {
      return;
    }
    this.form?.removeEventListener("variant:change", this.onFormVariantChange);
    this.form = form || null;
    this.form?.addEventListener("variant:change", this.onFormVariantChange);
  }

  parseChildrenConfig() {
    const script = this.querySelector("[data-bundle-children]");
    if (!script) {
      return [];
    }
    try {
      const parsed = JSON.parse(script.textContent || "null");
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.error("Bundle: failed to parse child products", error);
      return [];
    }
  }

  usableVariant(variant) {
    if (!variant) {
      return false;
    }
    if (variant.title && String(variant.title).includes("% off)")) {
      return false;
    }
    if (variant.metafields?.secomapp?.freegifts) {
      return false;
    }
    return Array.isArray(variant.options);
  }

  splitColors(colorName) {
    const value = String(colorName || "").trim();
    if (!value) {
      return [];
    }
    if (!value.includes("/")) {
      return [value];
    }
    return value.split("/").map((part) => part.trim()).filter(Boolean);
  }

  parentOptionNames() {
    if (!this.form) {
      return [];
    }
    return Array.from(this.form.elements)
      .filter((item) => item.matches("input[data-option-position]:checked"))
      .sort((a, b) => Number(a.getAttribute("data-option-position")) - Number(b.getAttribute("data-option-position")))
      .map((input) => (input.getAttribute("data-option-value") || "").trim());
  }

  childNonColorValues() {
    return Array.from(this.querySelectorAll(".product-bundle__item")).map((item) => {
      return Array.from(item.querySelectorAll("input[data-bundle-child-option]:checked")).map((input) => input.value);
    });
  }

  childOptionLists() {
    const parentOptions = this.parentOptionNames();
    const colors = this.splitColors(parentOptions[0] || "");
    const parentNonColor = parentOptions.slice(1);

    if (this.childrenConfig.length === 1) {
      const sharedSize = parentNonColor[0];
      return colors.map((color) => (sharedSize ? [color, sharedSize] : [color]));
    }

    const childValues = this.childNonColorValues();
    let colorIndex = 0;

    return this.childrenConfig.map((product, index) => {
      const totalOptionCount = product.variants?.[0]?.options?.length || 0;
      const options = [];

      if (colors[colorIndex]) {
        options.push(colors[colorIndex]);
        colorIndex += 1;
      } else if (colors.length > 0) {
        options.push(colors[colors.length - 1]);
      }

      const nonColorCount = Math.max(totalOptionCount - 1, 0);
      const selected = childValues[index] || [];
      for (let i = 0; i < nonColorCount; i += 1) {
        if (selected[i]) {
          options.push(selected[i]);
        }
      }
      return options;
    });
  }

  findVariant(product, optionValues) {
    if (!product?.variants || !optionValues?.length) {
      return null;
    }

    const matches = product.variants.filter((variant) => {
      if (!this.usableVariant(variant)) {
        return false;
      }
      if (variant.options.length === optionValues.length) {
        return variant.options.every((value, index) => value === optionValues[index]);
      }
      if (variant.options.length > optionValues.length) {
        return optionValues.every((value) => variant.options.includes(value));
      }
      return false;
    });

    return matches.find((variant) => variant.available) || matches[0] || null;
  }

  variantPrice(variant) {
    const amount = Number(variant?.price);
    if (!Number.isFinite(amount)) {
      return "0";
    }
    return String(amount / 100);
  }

  variantImage(variant) {
    if (!variant) {
      return "";
    }
    if (typeof variant.featured_image === "string") {
      return variant.featured_image;
    }
    return variant.featured_image?.src || variant.featured_media?.preview_image?.src || "";
  }

  sync() {
    this.bindForm();
    if (!this.childrenConfig.length) {
      this.setAttribute("data-match-ready", "0");
      return;
    }

    this.updateUnavailableOptions();
    this.updateParentSizeAvailability();
    this.updateOptionLabels();

    const optionLists = this.childOptionLists();
    const products = this.childrenConfig.length === optionLists.length
      ? this.childrenConfig
      : optionLists.map(() => this.childrenConfig[0]);

    const matched = products.map((product, index) => this.findVariant(product, optionLists[index]));
    const complete = matched.length > 0 && matched.every(Boolean);
    const soldOut = complete && matched.some((variant) => variant.available === false);

    this.setAttribute("data-match-ready", complete && !soldOut ? "1" : "0");
    this.updateChildImages(matched.filter(Boolean));
    this.updateFormProperties(complete ? matched : [], optionLists);
    this.updateAddToCartButtons(complete && !soldOut);
  }

  updateParentSizeAvailability() {
    if (this.childrenConfig.length !== 1 || !this.form) {
      return;
    }

    const product = this.childrenConfig[0];
    const colors = this.splitColors(this.parentOptionNames()[0] || "");
    const sizeInputs = Array.from(this.form.elements).filter((item) => {
      return item.matches("input[data-option-position]") && item.closest(".variant-picker__option--size");
    });

    sizeInputs.forEach((input) => {
      const size = (input.getAttribute("data-option-value") || "").trim();
      const allAvailable = colors.length > 0 && colors.every((color) => {
        const variant = this.findVariant(product, size ? [color, size] : [color]);
        return Boolean(variant?.available);
      });
      const label = input.labels?.[0] || this.form.querySelector(`label[for="${CSS.escape(input.id)}"]`);
      label?.classList.toggle("is-disabled", !allAvailable);
    });
  }

  updateOptionLabels() {
    this.querySelectorAll(".product-bundle__option").forEach((fieldset) => {
      const checked = fieldset.querySelector("input[data-bundle-child-option]:checked");
      const label = fieldset.querySelector("[data-bundle-option-label]");
      if (checked && label) {
        label.textContent = checked.value;
      }
    });
  }

  updateUnavailableOptions() {
    const optionLists = this.childOptionLists();
    const items = this.querySelectorAll(".product-bundle__item");

    items.forEach((item, index) => {
      const product = this.childrenConfig[index];
      const selectedOptions = optionLists[index] || [];
      if (!product?.variants) {
        return;
      }

      item.querySelectorAll("input[data-bundle-child-option]").forEach((input) => {
        const fieldset = input.closest(".product-bundle__option");
        const optionIndexInChild = Array.from(item.querySelectorAll(".product-bundle__option")).indexOf(fieldset);
        const testOptions = selectedOptions.slice();
        testOptions[optionIndexInChild + 1] = input.value;

        const available = Boolean(this.findVariant(product, testOptions)?.available);

        const label = item.querySelector(`label[for="${CSS.escape(input.id)}"]`);
        label?.classList.toggle("is-disabled", !available);
      });

      const fieldsets = item.querySelectorAll(".product-bundle__option");
      fieldsets.forEach((fieldset) => {
        const checked = fieldset.querySelector("input[data-bundle-child-option]:checked");
        const checkedLabel = checked ? item.querySelector(`label[for="${CSS.escape(checked.id)}"]`) : null;
        if (checkedLabel?.classList.contains("is-disabled")) {
          const firstAvailable = Array.from(fieldset.querySelectorAll("input[data-bundle-child-option]")).find((optionInput) => {
            const optionLabel = item.querySelector(`label[for="${CSS.escape(optionInput.id)}"]`);
            return optionLabel && !optionLabel.classList.contains("is-disabled");
          });
          if (firstAvailable) {
            firstAvailable.checked = true;
          }
        }
      });
    });
  }

  updateChildImages(matchedVariants) {
    const images = this.querySelectorAll(".product-bundle__image");
    matchedVariants.forEach((variant, index) => {
      const src = this.variantImage(variant);
      if (images[index] && src) {
        images[index].src = src;
      }
    });
  }

  updateFormProperties(matchedVariants, optionLists) {
    if (!this.form) {
      return;
    }
    const childrenInput = this.form.querySelector("[data-bundle-children-input]");
    const variantStrInput = this.form.querySelector("[data-bundle-variant-str-input]");
    if (!childrenInput || !variantStrInput) {
      return;
    }

    if (!matchedVariants.length) {
      childrenInput.value = "";
      variantStrInput.value = "";
      childrenInput.disabled = true;
      variantStrInput.disabled = true;
      return;
    }

    const children = matchedVariants.map((variant) => ({
      variant_id: String(variant.id),
      quantity: 1,
      price: this.variantPrice(variant)
    }));

    childrenInput.disabled = false;
    variantStrInput.disabled = false;
    childrenInput.value = JSON.stringify(children);
    variantStrInput.value = optionLists.map((options) => options.join(" / ")).join(",");
  }

  updateAddToCartButtons(ready) {
    const formId = this.getAttribute("form");
    if (!formId) {
      return;
    }

    const buttons = [
      ...Array.from(this.form?.querySelectorAll('[type="submit"]') || []),
      ...Array.from(document.querySelectorAll(`button[type="submit"][form="${CSS.escape(formId)}"]`))
    ];

    buttons.forEach((button) => {
      if (button.getAttribute("aria-busy") === "true") {
        return;
      }
      if (ready) {
        button.removeAttribute("disabled");
      } else {
        button.setAttribute("disabled", "disabled");
      }
    });
  }
}

if (!window.customElements.get("product-bundle")) {
  window.customElements.define("product-bundle", ProductBundle);
}
